import React, { useState, useEffect } from 'react';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useTheme } from '@/contexts/ThemeContext';
import { isNativeApp } from '@/utils/platform';
import MoonIcon from './icons/MoonIcon';
import SunIcon from './icons/SunIcon';
import ExploreIcon from './icons/ExploreIcon';
import LibraryIcon from './icons/LibraryIcon';
import MyBooksIcon from './icons/MyBooksIcon';
import ProfileIcon from './icons/ProfileIcon';

interface BurgerMenuOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToMyBooks?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToPricing?: () => void;
  isAuthenticated?: boolean;
}

const BurgerMenuOverlay: React.FC<BurgerMenuOverlayProps> = ({ isOpen, onClose, onNavigateToExplore, onNavigateToLibrary, onNavigateToMyBooks, onNavigateToProfile, onNavigateToLogin, onNavigateToSignup, onNavigateToTerms, onNavigateToPrivacy, onNavigateToAbout, onNavigateToPricing, isAuthenticated = false }) => {
  const { theme, toggleTheme } = useTheme();
  const isDarkMode = theme === 'dark';
  // Native app: hide the social links (menu cleanup for the app). The Pricing
  // link is shown on both since 1.2 — the app sells its subscriptions in-app,
  // and this is the way App Review (and everyone else) finds them.
  const native = isNativeApp();

  // In portrait (native app + mobile web) the primary destinations live here in
  // the menu instead of the header; on desktop the header still carries them.
  const go = (navigate?: () => void) => () => {
    navigate?.();
    onClose();
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Small delay to ensure the element is rendered before starting animation
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before hiding
      setTimeout(() => setIsVisible(false), 300);
    }
  }, [isOpen]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div
        className={`bg-black w-full h-full transition-opacity duration-300 ${
          isAnimating ? 'opacity-75' : 'opacity-0'
        }`}
      ></div>
      <div
        className={`fixed right-0 top-0 h-full w-80 shadow-xl transition-transform duration-300 ease-out overflow-y-auto overscroll-contain pt-safe pb-safe pr-safe ${
          isDarkMode ? 'bg-[var(--background-colour-dark-mode)]' : 'bg-white'
        } ${
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-6">
          <div className="flex justify-end items-center">
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                aria-label="Close navigation menu"
                className="hover:opacity-70 transition-opacity cursor-pointer"
                style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}
              >
                ✕
              </button>
            </div>
          </div>
          <nav className="space-y-4">
            {/* Profile sits at the top here; the header drops it whenever the burger is shown */}
            {isAuthenticated && (
              <>
                <button
                  onClick={go(onNavigateToProfile)}
                  aria-label="Go to profile page"
                  className="flex items-center gap-3 py-2 hover:text-blue-600 transition-colors text-left w-full cursor-pointer"
                  style={{ color: isDarkMode ? '#d1d5db' : '#4b5563' }}
                >
                  <ProfileIcon className="w-[22px] h-[22px]" size={22} aria-hidden="true" />
                  Profile
                </button>

                <div className="pt-2 border-t" style={{ borderColor: isDarkMode ? '#374151' : '#e5e7eb' }} />
              </>
            )}

            {/* Primary destinations (shown here in portrait; in the header on desktop) */}
            <button
              onClick={go(onNavigateToExplore)}
              aria-label="Go to explore page"
              className="flex items-center gap-3 py-2 hover:text-blue-600 transition-colors text-left w-full cursor-pointer"
              style={{ color: isDarkMode ? '#d1d5db' : '#4b5563' }}
            >
              <ExploreIcon className="w-6 h-6" size={24} />
              Explore
            </button>
            <button
              onClick={go(onNavigateToLibrary)}
              aria-label="Go to library page"
              className="flex items-center gap-3 py-2 hover:text-blue-600 transition-colors text-left w-full cursor-pointer"
              style={{ color: isDarkMode ? '#d1d5db' : '#4b5563' }}
            >
              <LibraryIcon className="w-5 h-5" size={22} />
              Library
            </button>
            {isAuthenticated && (
              <button
                onClick={go(onNavigateToMyBooks)}
                aria-label="Go to my books page"
                className="flex items-center gap-3 py-2 hover:text-blue-600 transition-colors text-left w-full cursor-pointer"
                style={{ color: isDarkMode ? '#d1d5db' : '#4b5563' }}
              >
                <MyBooksIcon className="w-5 h-5" size={22} />
                My Books
              </button>
            )}

            <div className="pt-2 border-t" style={{ borderColor: isDarkMode ? '#374151' : '#e5e7eb' }} />

            <button
              onClick={go(onNavigateToPricing)}
              aria-label="Navigate to pricing page"
              className="block py-2 hover:text-blue-600 transition-colors text-left w-full cursor-pointer"
              style={{ color: isDarkMode ? '#d1d5db' : '#4b5563' }}
            >
              Pricing
            </button>
            <button
              onClick={() => {
                onNavigateToAbout?.();
                onClose();
                setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
              }}
              aria-label="Navigate to about page"
              className="block py-2 hover:text-blue-600 transition-colors text-left w-full cursor-pointer"
              style={{ color: isDarkMode ? '#d1d5db' : '#4b5563' }}
            >
              About
            </button>
            <button
              onClick={() => {
                onNavigateToTerms?.();
                onClose();
                setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
              }}
              aria-label="Navigate to terms and conditions page"
              className="block py-2 hover:text-blue-600 transition-colors text-left w-full cursor-pointer"
              style={{ color: isDarkMode ? '#d1d5db' : '#4b5563' }}
            >
              Terms
            </button>
            <button
              onClick={() => {
                onNavigateToPrivacy?.();
                onClose();
                setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
              }}
              aria-label="Navigate to privacy notice page"
              className="block py-2 hover:text-blue-600 transition-colors text-left w-full cursor-pointer"
              style={{ color: isDarkMode ? '#d1d5db' : '#4b5563' }}
            >
              Privacy
            </button>
            <div className="pt-4 border-t" style={{ borderColor: isDarkMode ? '#374151' : '#e5e7eb' }}>
              {/* Social Links — hidden in the native app */}
              {!native && (
              <div className="mb-4 space-y-4">
                <a
                  href="https://substack.com/@hialeph"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  aria-label="Visit our Substack publication"
                  className="block py-2 hover:text-blue-600 transition-colors text-left w-full cursor-pointer"
                  style={{ color: isDarkMode ? '#d1d5db' : '#4b5563' }}
                >
                  Substack
                </a>
                <a
                  href="https://www.instagram.com/alephapp/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  aria-label="Follow us on Instagram"
                  className="block py-2 hover:text-blue-600 transition-colors text-left w-full cursor-pointer"
                  style={{ color: isDarkMode ? '#d1d5db' : '#4b5563' }}
                >
                  Instagram
                </a>
                <a
                  href="https://www.linkedin.com/company/hi-aleph/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  aria-label="Connect with us on LinkedIn"
                  className="block py-2 hover:text-blue-600 transition-colors text-left w-full cursor-pointer"
                  style={{ color: isDarkMode ? '#d1d5db' : '#4b5563' }}
                >
                  LinkedIn
                </a>
              </div>
              )}
              {/* Theme Toggle */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTheme();
                }}
                aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
                className="flex items-center py-2 hover:text-blue-600 transition-colors text-left w-full cursor-pointer"
                style={{ color: isDarkMode ? '#d1d5db' : '#4b5563' }}
              >
                <span className="mr-2">
                  {isDarkMode ? (
                    <SunIcon size={16} />
                  ) : (
                    <MoonIcon size={16} />
                  )}
                </span>
              </button>
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default BurgerMenuOverlay;
